/**
 * @fileoverview Input/output schemas for continuity-extractor-v2.
 */

import { z } from 'zod';
import { BaseFlowInputSchema } from '@/ai/agents/base-schema';
import { AGENT_SCHEMA_OBJECTS } from '@/ai/agents/prompt-variables';
import {
  ContinuityFactKindSchema,
  ContinuityFactResolutionTypeSchema,
} from '@/domains/story-planning/schemas/continuity-log';

// =============================================================================
// FLOW INPUT SCHEMA
// =============================================================================

/**
 * The extractor analyses one chapter at a time, given the facts that were
 * already active before the chapter began. The hook fills `activeContinuityFacts`
 * via the data-access map, scoped to facts whose lifecycle window covers the
 * chapter being analysed.
 */
export const InputSchema = z.object({
  ...BaseFlowInputSchema.shape,
  ...AGENT_SCHEMA_OBJECTS.chapterContent.schema.shape,
  ...AGENT_SCHEMA_OBJECTS.chapterNumber.schema.shape,
  ...AGENT_SCHEMA_OBJECTS.activeContinuityFacts.schema.shape,
  ...AGENT_SCHEMA_OBJECTS.charactersCurrent.schema.shape,
  ...AGENT_SCHEMA_OBJECTS.storyContext.schema.shape,
});

export type Input = z.infer<typeof InputSchema>;

// =============================================================================
// OUTPUT SCHEMA
// =============================================================================

/**
 * A proposed new fact. The hook fills in `id`, `createdAtChapterId`, `source`,
 * `proposedBy`, and `updatedAt` after the user accepts it, so the agent only
 * supplies the semantic content.
 */
export const ProposedContinuityFactSchema = z.object({
  kind: ContinuityFactKindSchema,
  summary: z.string().describe(
    'A short, agent-readable statement of the fact. Must stand on its own without ' +
    'requiring chapter context. Example: "Anna must deliver the report by May 3" ' +
    'rather than "the deadline mentioned earlier".',
  ),
  details: z.string().optional().describe('Optional longer prose if context matters.'),
  dueAt: z.string().optional().describe(
    'For deadlines: when the fact comes due, in story-readable form ("May 3", ' +
    '"by chapter 7", "before the trial").',
  ),
  counter: z.object({
    current: z.number().describe('Current count.'),
    limit: z.number().describe('The limit at which the count triggers a consequence.'),
  }).optional().describe('For counts (e.g. {current: 2, limit: 3} for two strikes).'),
  locationName: z.string().optional().describe('For locations: the place name.'),
  inWorldTime: z.string().optional().describe(
    'For in-world-time anchors: the in-fiction date/time established by this chapter.',
  ),
});

export const ProposedResolutionSchema = z.object({
  factId: z.string().describe('The id of the existing active fact being resolved.'),
  type: ContinuityFactResolutionTypeSchema.describe(
    'How the fact was resolved: satisfied (deadline met / promise kept), ' +
    'broken (deadline missed / promise broken), expired (no longer relevant), ' +
    'superseded (replaced by a newer fact).',
  ),
  note: z.string().optional().describe('Brief explanation grounded in the chapter text.'),
});

export const OutputSchema = z.object({
  newFacts: z.array(ProposedContinuityFactSchema).describe(
    'Binding facts established for the first time in this chapter. Empty array if none.',
  ),
  resolutions: z.array(ProposedResolutionSchema).describe(
    'Resolutions of facts that were active before this chapter. Only reference facts ' +
    'present in the supplied activeContinuityFacts list. Empty array if none.',
  ),
});

export type Output = z.infer<typeof OutputSchema>;
export type ProposedContinuityFact = z.infer<typeof ProposedContinuityFactSchema>;
export type ProposedResolution = z.infer<typeof ProposedResolutionSchema>;
